import React, { useEffect, useMemo, useState } from 'react'
import {
  CompetencyEvaluation,
  SubCompetencyWithEvaluation,
  UpdateCompetencyEvaluationPayload,
} from '../../types/GrowthWheel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave } from '@fortawesome/free-solid-svg-icons'
import { useUpdateCompetencyEvaluationMutation } from '../../redux/growth-wheel.endpoints'
import SelfAssessmentComponent from './SelfAssessmentComponent'
import GuidedAssessmentComponent from './GuidedAssessmentComponent'
import { evaluateGrowthWheelCompletion } from '../../redux/growthWheel.slice'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../redux/store'

interface SubCompetencyAssessmentContentProps {
  competency: SubCompetencyWithEvaluation
  subjectUserId: number
  /** Logged-in reviewer id when saving guided assessment (admin view). */
  reviewerUserId?: number
  canEditSelf?: boolean
  canEditGuided?: boolean
  isLocked?: boolean
  userRole?: 'admin' | 'user'
}

interface AssessmentScoreState {
  selfLevel: number | null
  leaderLevel: number | null
  selfNotes: string
  guidedNotes: string
}

const getScoreFromEvaluation = (
  evaluation?: CompetencyEvaluation | null,
): AssessmentScoreState => ({
  selfLevel: evaluation?.selfLevel ?? null,
  leaderLevel: evaluation?.leaderLevel ?? null,
  selfNotes: evaluation?.selfNote ?? '',
  guidedNotes: evaluation?.leaderNote ?? '',
})

const normalizeLevelDescriptions = (
  input:
    | SubCompetencyWithEvaluation['levelDescriptions']
    | string[]
    | undefined,
) => {
  if (!input) {
    return {}
  }

  if (Array.isArray(input)) {
    return input.reduce<Record<number, string>>((acc, description, index) => {
      const level = index + 1
      acc[level] = description
      return acc
    }, {})
  }

  return input
}

const SubCompetencyAssessmentContent = ({
  competency,
  subjectUserId,
  reviewerUserId,
  canEditSelf = true,
  canEditGuided = false,
  isLocked = false,
  userRole = 'user',
}: SubCompetencyAssessmentContentProps) => {
  const dispatch = useDispatch()
  const assessmentSession = useSelector(
    (state: RootState) => state.growthWheel.assessmentSession,
  )
  const normalizedLevelDescriptions = useMemo(
    () => normalizeLevelDescriptions(competency.levelDescriptions),
    [competency.levelDescriptions],
  )
  const levels = useMemo(() => {
    const keys = Object.keys(normalizedLevelDescriptions)
      .map(Number)
      .filter(value => !Number.isNaN(value))
      .sort((a, b) => a - b)
    return keys.length ? keys : [1, 2, 3, 4, 5]
  }, [normalizedLevelDescriptions])

  const [currentScore, setCurrentScore] = useState<AssessmentScoreState>(() =>
    getScoreFromEvaluation(competency.evaluation),
  )
  const [initialScore, setInitialScore] = useState<AssessmentScoreState>(() =>
    getScoreFromEvaluation(competency.evaluation),
  )

  const [saveMessage, setSaveMessage] = useState('')
  const [updateCompetencyEvaluation, { isLoading: isSaving }] =
    useUpdateCompetencyEvaluationMutation()

  const defaultLevelDescriptions = useMemo(() => {
    if (Object.keys(normalizedLevelDescriptions).length) {
      return normalizedLevelDescriptions
    }
    return {}
  }, [normalizedLevelDescriptions])

  const [levelDescriptions, setLevelDescriptions] = useState<
    Record<number, string>
  >(defaultLevelDescriptions)

  useEffect(() => {
    const evaluationScore = getScoreFromEvaluation(competency.evaluation)
    setCurrentScore(evaluationScore)
    setInitialScore(evaluationScore)
    setSaveMessage('')
    setLevelDescriptions(defaultLevelDescriptions)
  }, [
    competency.id,
    competency.evaluation,
    normalizedLevelDescriptions,
    defaultLevelDescriptions,
  ])

  const hasChanges = useMemo(
    () => JSON.stringify(currentScore) !== JSON.stringify(initialScore),
    [initialScore, currentScore],
  )

  const handleScoreChange = (
    field: keyof AssessmentScoreState,
    value: number | string,
  ) => {
    setCurrentScore(prev => ({
      ...prev,
      [field]: value,
    }))
    setSaveMessage('')
  }

  const handleCancel = () => {
    setCurrentScore(initialScore)
    setSaveMessage('')
  }

  const evaluationPayload = () => {
    const payload = effectiveCanEditSelf
      ? {
          selfLevel: currentScore.selfLevel,
          selfNote: currentScore.selfNotes || null,
        }
      : effectiveCanEditGuided
      ? {
          leaderLevel: currentScore.leaderLevel,
          leaderNote: currentScore.guidedNotes || null,
          leaderId: reviewerUserId ?? null,
        }
      : null

    return payload as UpdateCompetencyEvaluationPayload
  }

  const handleSave = async () => {
    const evaluationPayloadValue = evaluationPayload()
    if (!evaluationPayloadValue) {
      return
    }
    if (isLocked) {
      setSaveMessage('Completed assessments are locked')
      return
    }
    try {
      if (effectiveCanEditSelf && currentScore.selfLevel === null) {
        setSaveMessage('Cannot save: self level is not selected')
        return
      }

      if (effectiveCanEditGuided && currentScore.leaderLevel === null) {
        setSaveMessage('Cannot save: guided level is not selected')
        return
      }

      await updateCompetencyEvaluation({
        evaluationId: competency.evaluation?.id ?? 0,
        userId: subjectUserId,
        subCompetencyId: competency.id,
        assessmentSessionId: assessmentSession?.id,
        payload: evaluationPayloadValue,
      }).unwrap()
      setInitialScore(currentScore)
      setSaveMessage('Saved successfully')
      dispatch(evaluateGrowthWheelCompletion())
    } catch {
      setSaveMessage('Failed to save. Please try again.')
    }
  }

  const selfAssessmentPending =
    userRole === 'admin' && currentScore.selfLevel === null
  const effectiveCanEditSelf = canEditSelf && !isLocked
  const effectiveCanEditGuided =
    canEditGuided && !isLocked && !selfAssessmentPending

  return (
    <div>
      {isLocked && (
        <div className="alert alert-secondary py-3 small mb-3">
          This assessment is locked. Scores and notes are read-only.
        </div>
      )}
      {selfAssessmentPending && (
        <div className="alert alert-warning py-3 small mb-3">
          Unable to score guided assesment: the user has not made their self
          assessment.
        </div>
      )}
      <div className="row g-4">
        <div className="col-lg-6">
          <div className="card shadow-sm gw-assessment-card gw-assessment-card--self h-100">
            <SelfAssessmentComponent
              levels={levels}
              canEditSelf={effectiveCanEditSelf}
              handleScoreChange={handleScoreChange}
              currentScore={currentScore}
              levelDescriptions={levelDescriptions}
            />
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card shadow-sm gw-assessment-card gw-assessment-card--guided h-100">
            <GuidedAssessmentComponent
              levels={levels}
              canEditGuided={effectiveCanEditGuided}
              handleScoreChange={handleScoreChange}
              currentScore={currentScore}
              levelDescriptions={levelDescriptions}
              selfAssessmentPending={selfAssessmentPending}
            />
          </div>
        </div>
      </div>

      <div className="card shadow-sm mt-4 gw-assessment-actions">
        <div className="card-body d-flex align-items-center justify-content-between">
          <div className="d-flex flex-column gap-1">
            {saveMessage && (
              <span className="small text-success d-flex align-items-center gap-2">
                <FontAwesomeIcon icon={faSave} />
                {saveMessage}
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            {hasChanges && (
              <button onClick={handleCancel} className="gw-btn-cancel">
                Cancel
              </button>
            )}
            <button
              onClick={() => handleSave()}
              disabled={!hasChanges || isSaving || isLocked}
              className={`gw-btn-submit ${
                !hasChanges || isSaving || isLocked ? 'disabled' : ''
              }`}
            >
              <FontAwesomeIcon icon={faSave} className="me-1" />
              {isSaving ? 'Saving...' : 'Save & Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubCompetencyAssessmentContent
